import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Link,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
} from '@mui/material';
import {
    DataGridPremium,
    GridCellParams,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiHelpCircleOutline } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchInTransitDelayPickupParties } from '../../state/features/networkHealthSlice';
import { privateRoutes } from '../../routes/appRouteList';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus, Grade } from '../../utils/enums';
import { buildShipperHealthLink } from '../../utils/navigationHelpers';
import { formatNumber } from '../../utils/formattingUtils';
import { getGradeColor } from '../../utils/styleUtils';
import GradeLabel from '../Labels/GradeLabel';

const classesPrefix = 'inTransitDelayCard';

const classes = {
    cardHeader: `${classesPrefix}-cardHeader`,
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    tableWrapper: `${classesPrefix}-tableWrapper`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledCard = styled(Card)(({ theme }) => {
    return {
        [`& .${classes.cardHeader}`]: {
            paddingBottom: theme.spacing(0),
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: theme.palette.action.hover,
            },
        },
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
        [`& .${classes.tableWrapper}`]: {
            height: '300px',
            '& .inTransitDelayTable.gradeA': {
                color: getGradeColor(Grade.A, theme),
            },
            '& .inTransitDelayTable.gradeB': {
                color: getGradeColor(Grade.B, theme),
            },
            '& .inTransitDelayTable.gradeC': {
                color: getGradeColor(Grade.C, theme),
            },
            '& .inTransitDelayTable.gradeD': {
                color: getGradeColor(Grade.D, theme),
            },
            '& .inTransitDelayTable.gradeE': {
                color: getGradeColor(Grade.E, theme),
            },
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const InTransitDelayCard = (): React.JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // TODO: TCSC-50774
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const inTransitDelayPickupPartiesApiStatus = useAppSelector((state) => { return state.networkHealth.inTransitDelayPickupPartiesApiStatus; });
    // const inTransitDelayPickupPartiesApiParams = useAppSelector((state) => { return state.networkHealth.inTransitDelayPickupPartiesApiParams; }); // TODO: TCSC-50774
    const inTransitDelayPickupParties = useAppSelector((state) => { return state.networkHealth.inTransitDelayPickupParties; });

    useEffect(() => {
        // TODO: TCSC-50774
        // dispatch(fetchInTransitDelayPickupParties(inTransitDelayPickupPartiesApiParams));
        dispatch(fetchInTransitDelayPickupParties());
    }, [dispatch]);

    const inTransitDelayTooltip = useMemo((): React.JSX.Element => {
        return (
            <div>
                <Typography variant='body2' gutterBottom className={classes.helpText}>
                    <span className={classes.title}>In Transit Delay</span>
                    <span> compares actual transit time to a standard transit time governed by Hours of Service (HOS) rules.  The calculation first determines the difference between actual order execution hours and the standard, expected execution hours. Next the difference is divided by the actual order execution hours. The quotient produces an In Transit ratio where zero is preferred.</span>
                </Typography>
                <Typography variant='body2' className={classes.helpText}>
                    The grade thresholds are based on this delay ratio:
                </Typography>
                <ul>
                    <li>
                        <span className={classes.title}>A</span>
                        <span> = Less than 0</span>
                    </li>
                    <li>
                        <span className={classes.title}>C</span>
                        <span> = Between 0 and 0.15</span>
                    </li>
                    <li>
                        <span className={classes.title}>E</span>
                        <span> = Greater than 0.15</span>
                    </li>
                </ul>
            </div>
        );
    }, []);

    const columns = useMemo((): GridColDef<Shipper>[] => {
        return [
            {
                headerName: 'Shipper',
                field: 'pickup_party_name',
                sortable: false,
                hideSortIcons: true,
                flex: 1,
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperHealthLink(row.pickup_party_id)}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: 'Avg. Delay',
                description: 'Average Delay',
                field: 'average_netwise_in_transit_delay_hours_duration',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip title={inTransitDelayTooltip}>
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-50774
                valueGetter: (_value, row): number | null => {
                    return row.netwise_in_transit_delay_hours_duration !== null && row.freight_order_count ?
                        row.netwise_in_transit_delay_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params: GridCellParams<Shipper, number>): string => {
                    return `inTransitDelayTable grade${params.row.average_netwise_in_transit_delay_hours_ratio_grade}`;
                },
            },
        ];
    }, [inTransitDelayTooltip]);

    const handlePaginationModelChange = (model: GridPaginationModel): void => {
        // TODO: TCSC-50774
        // dispatch(updateInTransitDelayPickupPartiesApiParams({
        //     // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
        //     pageNumber: model.page + 1,
        //     pageSize: model.pageSize,
        // }));

        setPage(model.page);
        setPageSize(model.pageSize);
    };

    const handleInTransitDelayCardClick = (): void => {
        navigate(privateRoutes.shipperHealth);
    };

    return (
        <StyledCard>
            <CardHeader
                classes={{
                    root: classes.cardHeader,
                    content: classes.cardHeaderContent,
                }}
                title={
                    <Fragment>
                        <Typography variant='h4'>In Transit Delay</Typography>
                        <GradeLabel
                            apiStatus={inTransitDelayPickupPartiesApiStatus}
                            grade={inTransitDelayPickupParties.pickup_party_health.netwise_in_transit_delay_hours_ratio_grade}
                            variant='h2'
                        />
                    </Fragment>
                }
                disableTypography
                action={
                    <StyledTooltip title={inTransitDelayTooltip}>
                        <IconButton size='small' color='primary'>
                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                        </IconButton>
                    </StyledTooltip>
                }
                onClick={handleInTransitDelayCardClick}
            />

            <CardContent className={classes.cardContent}>
                <div className={classes.tableWrapper}>
                    <DataGridPremium
                        rows={inTransitDelayPickupParties.pickup_parties}
                        columns={columns}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableRowGrouping
                        disableAggregation
                        loading={inTransitDelayPickupPartiesApiStatus === ApiStatus.Idle || inTransitDelayPickupPartiesApiStatus === ApiStatus.Loading}
                        getRowId={(row: Shipper): string => { return row.pickup_party_id; }}
                        // rowCount={inTransitDelayPickupParties.pickup_party_count}
                        pagination
                        // paginationMode='server'
                        paginationModel={{
                            // TODO: TCSC-50774
                            // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                            // page: inTransitDelayPickupPartiesApiParams.pageNumber - 1,
                            // pageSize: inTransitDelayPickupPartiesApiParams.pageSize,
                            page,
                            pageSize,
                        }}
                        onPaginationModelChange={handlePaginationModelChange}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        initialState={{
                            density: 'compact',
                            // TODO: TCSC-50774
                            sorting: {
                                sortModel: [
                                    { field: 'average_netwise_in_transit_delay_hours_duration', sort: 'desc' },
                                    { field: 'pickup_party_name', sort: 'asc' },
                                ],
                            },
                        }}
                    />
                </div>
            </CardContent>
        </StyledCard>
    );
};

export default InTransitDelayCard;
